import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import Layout from '../../components/layout/Layout';
import Section from '../../components/layout/Section';
import Container from '../../components/layout/Container';

// Section components
import CallToAction from '../../components/sections/CallToAction';

// UI Components
import Card from '../../components/ui/Card';

// Data
import { industrySolutions } from '../../data/mockData';

/**
 * Industries listing page component
 */
const IndustriesPage: React.FC = () => {
  // Function to get industry-specific icons
  const getIndustryIcon = (industryId: string) => {
    switch (industryId) {
      case 'healthcare':
        return (
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        );
      case 'finance':
        return (
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'retail':
        return (
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.1 5.5M7 13v8a2 2 0 002 2h6a2 2 0 002-2v-8m-8 0V9a2 2 0 012-2h4a2 2 0 012 2v4.1" />
          </svg>
        );
      case 'manufacturing':
        return (
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      case 'education':
        return (
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18.1 7.5 18.1s3.332.477 4.5 1.1m0-13C13.168 5.477 14.754 5 16.5 5s3.332.477 4.5 1.253v13C19.832 18.477 18.246 18.1 16.5 18.1s-3.332.477-4.5 1.1" />
          </svg>
        );
      case 'legal':
        return (
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
          </svg>
        );
      default:
        return (
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2-2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
    }
  };

  return (
    <Layout>
      {/* Hero section */}
      <div className="relative overflow-hidden bg-neu-bg pt-28 pb-14">
        <div
          className="pointer-events-none absolute top-0 right-0 h-full flex items-center opacity-50 overflow-hidden"
          style={{ width: '38%' }}
        >
          <div
            className="relative flex items-center justify-center"
            style={{ width: 380, height: 380, marginLeft: 'auto', marginRight: '-80px' }}
          >
            <div className="absolute rounded-full" style={{ width: 380, height: 380, boxShadow: '10px 10px 22px rgb(163,177,198,0.5), -10px -10px 22px rgba(255,255,255,0.4)', background: '#E0E5EC' }} />
            <div className="absolute rounded-full" style={{ width: 260, height: 260, boxShadow: 'inset 7px 7px 14px rgb(163,177,198,0.6), inset -7px -7px 14px rgba(255,255,255,0.5)', background: '#E0E5EC' }} />
            <div className="absolute rounded-full" style={{ width: 140, height: 140, boxShadow: '7px 7px 14px rgb(163,177,198,0.6), -7px -7px 14px rgba(255,255,255,0.5)', background: '#E0E5EC' }} />
            <div className="absolute rounded-full animate-float" style={{ width: 56, height: 56, background: 'linear-gradient(135deg, #6C63FF 0%, #8B84FF 100%)', boxShadow: '0 4px 16px rgba(108,99,255,0.45)' }} />
          </div>
        </div>
        <Container>
          <motion.div
            className="relative z-10 max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-neu-text mb-6 leading-tight">
              Industry-Specific AI Solutions
            </h1>
            <p className="text-lg md:text-xl text-neu-muted mb-8 max-w-2xl mx-auto leading-relaxed">
              Tailored AI implementations that address the unique challenges and opportunities in your industry.
            </p>
          </motion.div>
        </Container>
      </div>
      
      {/* Industries grid */}
      <Section bgColor="bg-white" padding="lg">
        <Container>
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {industrySolutions.map((industry, index) => (
                <motion.div
                  key={industry.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex"
                >
                  <Link to={industry.ctaLink} className="w-full h-full">
                    <Card hover className="h-full flex flex-col">
                      <div className="p-6 md:p-8 flex-grow">
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary/10 text-primary">
                            {getIndustryIcon(industry.id)}
                          </div>
                          <div className="ml-4">
                            <h2 className="text-xl font-semibold">{industry.title}</h2>
                          </div>
                        </div>

                        <p className="text-neutral-700 mb-6">{industry.description}</p>

                        <div className="mb-6">
                          <h4 className="font-semibold text-neutral-800 mb-3 text-sm">Key Use Cases:</h4>
                          <ul className="space-y-2">
                            {industry.useCases.slice(0, 3).map((useCase, idx) => (
                              <li key={idx} className="flex items-start text-sm text-neutral-600">
                                <div className="flex-shrink-0 w-4 h-4 mr-2 mt-0.5">
                                  <svg className="w-full h-full text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                                <span>{useCase}</span>
                              </li>
                            ))}
                            {industry.useCases.length > 3 && (
                              <li className="text-primary font-medium text-sm">+{industry.useCases.length - 3} more</li>
                            )}
                          </ul>
                        </div>

                        <div className="mt-auto pt-2">
                          <span className="inline-flex items-center text-primary font-medium text-sm">
                            Learn more
                            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Stats Section */}
      <Section bgColor="bg-neu-bg" padding="lg">
        <Container>
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neu-text">Trusted Across Industries</h2>
            <p className="text-lg text-neu-muted">Our industry expertise delivers measurable results</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: 'Industries Served', value: '6+' },
              { label: 'Solutions Deployed', value: '50+' },
              { label: 'Client Success Rate', value: '98%' },
              { label: 'Average ROI', value: '3.5x' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-4xl md:text-5xl font-bold mb-2 text-gradient-neu">{stat.value}</div>
                <div className="text-neu-text font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Call to action */}
      <CallToAction
        title="Ready to Transform Your Business with AI?"
        description="Schedule a complimentary consultation to discuss how our AI solutions can address your industry's unique challenges."
        ctaText="Book Your Free Consultation"
        ctaLink="/contact"
      />
    </Layout>
  );
};

export default IndustriesPage;